import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, BarChart } from '../components/charts';
import {
  Loader2,
  ArrowUp,
  ArrowDown,
  Zap,
  DollarSign,
  ShoppingCart,
  Target,
  Users,
  Activity,
  BarChart3,
  Radio,
  X
} from 'lucide-react';

// Executive Wall – overlay, minimal, top-manager view
export default function ExecutiveWall() {
  const [dateRange] = useState('last7');
  const [isLoading, setIsLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');

  const [googleAdsData, setGoogleAdsData] = useState([]);
  const [metaAdsData, setMetaAdsData] = useState([]);
  const [trafficData, setTrafficData] = useState(null);

  const getBrandId = () =>
    typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    if (num >= 1_000_000) return `₺${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `₺${(num / 1_000).toFixed(1)}K`;
    return `₺${Math.round(num).toLocaleString('tr-TR')}`;
  };

  const formatNumber = (value) => {
    const num = Number(value) || 0;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return Math.round(num).toLocaleString('tr-TR');
  };

  const fetchLogo = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      const res = await fetch(`https://api.glowytics.com/api/brand-info/${brandId}/details`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data?.logoUrl || '/logoicon.png');
      }
    } catch {
      /* noop */
    }
  };

  const fetchGoogle = async () => {
    const brandId = getBrandId();
    if (!brandId) return [];
    const url = `https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance?dateRange=${dateRange}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
    });
    if (!res.ok) return [];
    return res.json();
  };

  const fetchMeta = async () => {
    const brandId = getBrandId();
    if (!brandId) return [];
    const url = `https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
    });
    if (!res.ok) return [];
    return res.json();
  };

  const fetchTraffic = async () => {
    const brandId = getBrandId();
    if (!brandId) return null;
    const url = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${dateRange}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
    });
    if (!res.ok) return null;
    return res.json();
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchLogo();
      const [g, m, t] = await Promise.all([fetchGoogle(), fetchMeta(), fetchTraffic()]);
      setGoogleAdsData(Array.isArray(g) ? g : []);
      setMetaAdsData(Array.isArray(m) ? m : []);
      setTrafficData(t);
      setIsLoading(false);
    };
    load();
  }, [dateRange]);

  const googleTotals = useMemo(() => {
    return googleAdsData.reduce(
      (acc, d) => ({
        cost: acc.cost + (Number(d.cost) || 0),
        revenue: acc.revenue + (Number(d.ga4_total_revenue) || 0),
        conversions: acc.conversions + (Number(d.conversions) || 0)
      }),
      { cost: 0, revenue: 0, conversions: 0 }
    );
  }, [googleAdsData]);

  const metaTotals = useMemo(() => {
    return metaAdsData.reduce(
      (acc, d) => ({
        cost: acc.cost + (Number(d.spend) || 0),
        revenue: acc.revenue + (Number(d.ga4_total_revenue) || 0),
        conversions: acc.conversions + (Number(d.conversions) || Number(d.purchases) || 0)
      }),
      { cost: 0, revenue: 0, conversions: 0 }
    );
  }, [metaAdsData]);

  const totals = useMemo(() => {
    const spend = googleTotals.cost + metaTotals.cost;
    const revenue = googleTotals.revenue + metaTotals.revenue;
    const orders = googleTotals.conversions + metaTotals.conversions;
    const roas = spend > 0 ? revenue / spend : 0;
    return { spend, revenue, orders, roas };
  }, [googleTotals, metaTotals]);

  const chartData = useMemo(() => {
    const allDates = [...new Set([...googleAdsData.map((d) => d.date), ...metaAdsData.map((d) => d.date)])].sort();
    const labels = allDates.map((d) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }));
    const revenueSeries = allDates.map((date) => {
      const g = googleAdsData.find((d) => d.date === date);
      const m = metaAdsData.find((d) => d.date === date);
      return (Number(g?.ga4_total_revenue) || 0) + (Number(m?.ga4_total_revenue) || 0);
    });
    const spendSeries = allDates.map((date) => {
      const g = googleAdsData.find((d) => d.date === date);
      const m = metaAdsData.find((d) => d.date === date);
      return (Number(g?.cost) || 0) + (Number(m?.spend) || 0);
    });
    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueSeries,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16,185,129,0.12)',
          fill: true,
          tension: 0.45
        },
        {
          label: 'Ad Spend',
          data: spendSeries,
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245,158,11,0.12)',
          fill: true,
          tension: 0.45
        }
      ]
    };
  }, [googleAdsData, metaAdsData]);

  const trafficSources = useMemo(() => {
    if (!trafficData?.detailedData) return [];
    const map = {};
    trafficData.detailedData.forEach((row) => {
      const source = row.source || 'direct';
      map[source] = map[source] || { sessions: 0, revenue: 0 };
      map[source].sessions += Number(row.sessions) || 0;
      map[source].revenue += Number(row.totalRevenue) || 0;
    });
    const total = Object.values(map).reduce((s, v) => s + v.sessions, 0);
    return Object.entries(map)
      .map(([k, v]) => ({
        name: k,
        sessions: v.sessions,
        revenue: v.revenue,
        pct: total > 0 ? (v.sessions / total) * 100 : 0
      }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 5);
  }, [trafficData]);

  const shimmering = 'bg-gradient-to-r from-white/5 via-white/20 to-white/5 animate-pulse';

  const navigate = useNavigate();
  const close = () => navigate(-1);

  // Fallbacks to keep chart lengths aligned
  const safeLabels = chartData.labels.length ? chartData.labels : Array(7).fill('');
  const safeDataset = (arr) => (arr.length ? arr : Array(safeLabels.length).fill(0));
  const dailyRevenueFull = safeDataset(chartData.datasets[0]?.data || []);
  const dailySpendFull = safeDataset(chartData.datasets[1]?.data || []);
  const dailyOrdersFull = safeDataset(getOrdersSeries(googleAdsData, metaAdsData));
  const lastLen = Math.min(10, safeLabels.length, dailyRevenueFull.length, dailySpendFull.length, dailyOrdersFull.length);
  const lastLabels = safeLabels.slice(-lastLen);
  const dailyRevenue = dailyRevenueFull.slice(-lastLen);
  const dailySpend = dailySpendFull.slice(-lastLen);
  const dailyOrders = dailyOrdersFull.slice(-lastLen);

  const sourceLogos = {
    google: { name: 'Google Ads', badge: 'G' },
    meta: { name: 'Meta Ads', badge: 'M' },
    'meta ads': { name: 'Meta Ads', badge: 'M' },
    'paid_social': { name: 'Paid Social', badge: 'PS' },
    direct: { name: 'Direct', badge: 'D' },
    organic: { name: 'Organic', badge: 'O' },
    ga4: { name: 'GA4', badge: 'GA' }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-slate-900/85 backdrop-blur-md flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-300 mx-auto mb-4" />
          <p className="text-slate-300">Executive wall loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/88 backdrop-blur-xl flex items-center justify-center">
      <div className="absolute inset-0" onClick={close} aria-label="Close overlay" />
      <div className="relative w-full max-w-7xl mx-auto bg-slate-900 text-white rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
        <button
          onClick={close}
          className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-white/10 border border-white/20 text-slate-200 hover:bg-white/20"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 md:p-8">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt="Brand" className="w-12 h-12 rounded-xl object-contain bg-white/5 p-2" />}
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400">Executive view</p>
                <h1 className="text-2xl font-bold">Performance Wall</h1>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-300">
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Last 7 Days</span>
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-200 border border-emerald-500/30 flex items-center gap-2">
                <Radio className="w-3 h-3" /> Live Ready
              </span>
            </div>
          </div>

          {/* Hero metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <MetricCard title="Revenue" value={formatCurrency(totals.revenue)} icon={DollarSign} accent="emerald" />
            <MetricCard title="Ad Spend" value={formatCurrency(totals.spend)} icon={Zap} accent="amber" />
            <MetricCard
              title="ROAS"
              value={`${totals.roas.toFixed(2)}x`}
              icon={Target}
              accent={totals.roas >= 3 ? 'emerald' : totals.roas >= 1 ? 'amber' : 'rose'}
            />
            <MetricCard title="Orders" value={formatNumber(totals.orders)} icon={ShoppingCart} accent="blue" />
          </div>

          {/* Charts and sources */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 bg-slate-800/80 border border-white/5 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Revenue vs Ad Spend</h2>
                <div className="flex items-center gap-3 text-xs text-slate-300">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    Revenue
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    Ad Spend
                  </span>
                </div>
              </div>
              <div className="h-80">
                <LineChart
                  data={{ ...chartData, labels: safeLabels, datasets: chartData.datasets }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: '#cbd5e1' } },
                      y: { grid: { color: 'rgba(255,255,255,0.08)' }, ticks: { color: '#cbd5e1' } }
                    }
                  }}
                />
              </div>
            </div>

            <div className="bg-slate-800/80 border border-white/5 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Top Traffic</h2>
                <Users className="w-5 h-5 text-indigo-200" />
              </div>
              {trafficSources.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-slate-400">No traffic data</div>
              ) : (
                <div className="space-y-4">
                  {trafficSources.map((s, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize flex items-center gap-2">
                          <Badge source={s.name} map={sourceLogos} />
                          {s.name}
                        </span>
                        <span className="text-xs text-slate-300">{s.pct.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(s.pct, 100)}%`,
                            backgroundColor: ['#818cf8', '#22d3ee', '#f59e0b', '#10b981', '#ef4444'][idx % 5]
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-300 mt-2">
                        <span>{formatNumber(s.sessions)} sessions</span>
                        <span>{formatCurrency(s.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Daily bars */}
          <div className="bg-slate-800/80 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Daily Snapshot</h2>
              <Activity className="w-5 h-5 text-emerald-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SparkBar
                title="Revenue"
                color="#10B981"
                data={dailyRevenue}
                labels={lastLabels}
                fmt={formatCurrency}
              />
              <SparkBar
                title="Ad Spend"
                color="#F59E0B"
                data={dailySpend}
                labels={lastLabels}
                fmt={formatCurrency}
              />
              <SparkBar
                title="Orders"
                color="#38BDF8"
                data={dailyOrders}
                labels={lastLabels}
                fmt={formatNumber}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MetricCard = ({ title, value, icon: Icon, accent = 'emerald' }) => {
  const bgMap = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-500/5 border-amber-500/30',
    rose: 'from-rose-500/20 to-rose-500/5 border-rose-500/30',
    blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30'
  };
  const iconMap = {
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    rose: 'text-rose-300',
    blue: 'text-blue-300'
  };
  return (
    <div className={`bg-gradient-to-br ${bgMap[accent] || bgMap.emerald} border rounded-2xl p-5`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-200/80">{title}</p>
        <div className="p-2 rounded-lg bg-white/10">
          <Icon className={`w-5 h-5 ${iconMap[accent] || iconMap.emerald}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};

const SparkBar = ({ title, data, labels, color, fmt }) => {
  const maxVal = Math.max(...(data?.length ? data : [0]));
  const chart = {
    labels,
    datasets: [
      {
        data,
        backgroundColor: color,
        borderRadius: 6,
        maxBarThickness: 22,
        categoryPercentage: 0.6,
        barPercentage: 0.7
      }
    ]
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-sm text-slate-200/80 mb-3">{title}</p>
      <div className="h-40">
        <BarChart
          data={chart}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => fmt(ctx.raw) } } },
            scales: {
              x: { display: false },
              y: {
                display: false,
                beginAtZero: true,
                suggestedMax: maxVal > 0 ? maxVal * 1.2 : 1
              }
            }
          }}
        />
      </div>
    </div>
  );
};

const getOrdersSeries = (googleAdsData, metaAdsData) => {
  const allDates = [...new Set([...googleAdsData.map((d) => d.date), ...metaAdsData.map((d) => d.date)])].sort();
  return allDates.map((date) => {
    const g = googleAdsData.find((d) => d.date === date);
    const m = metaAdsData.find((d) => d.date === date);
    return (Number(g?.conversions) || 0) + (Number(m?.conversions) || 0) + (Number(m?.purchases) || 0);
  });
};

// Small badge for traffic sources
const Badge = ({ source, map }) => {
  if (!source) return null;
  const key = (source || '').toString().toLowerCase();
  const meta = map?.[key];
  if (!meta) return (
    <span className="inline-flex items-center justify-center w-6 h-6 text-[10px] font-semibold rounded-full bg-white/10 text-slate-100">
      {key.slice(0, 2).toUpperCase() || '?'}
    </span>
  );
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 text-[10px] font-semibold rounded-full bg-white/10 text-slate-100">
      {meta.badge || meta.name?.[0] || '?'}
    </span>
  );
};
